import { useEffect, useCallback, useState } from "react";
import { Star, Quote, Heart, ChevronLeft, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useSiteContent } from "@/hooks/useSiteContent";
import { useTestimonials, type Testimonial } from "@/hooks/useTestimonials";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";

const getContentValue = (content: Record<string, string> | undefined, key: string, defaultValue: string) => {
  return content?.[key] || defaultValue;
};

const getInitials = (name: string) => {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();
};

const Testimonials = () => {
  const { data: sectionContent } = useSiteContent("testimonials_content");
  const { testimonials, isLoading } = useTestimonials();
  const [api, setApi] = useState<CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  
  const content = sectionContent?.value as Record<string, string> | undefined;
  const badge = getContentValue(content, "badge", "Customer Love");
  const title = getContentValue(content, "title", "Trusted by Thousands of Happy Customers");
  const description = getContentValue(content, "description", "Real stories from real people who brought the beauty of rural craftsmanship into their homes.");
  const customerCount = getContentValue(content, "customer_count", "2,500+");

  // Calculate average rating from database testimonials
  const averageRating = testimonials && testimonials.length > 0
    ? (testimonials.reduce((acc, t) => acc + t.rating, 0) / testimonials.length).toFixed(1)
    : "4.8";

  const onSelect = useCallback(() => {
    if (!api) return;
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, [api]);

  useEffect(() => {
    if (!api) return;
    onSelect();
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api, onSelect]);

  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);

  // JSON-LD structured data for SEO
  const structuredData = testimonials && testimonials.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Gaonova",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": averageRating,
      "reviewCount": testimonials.length,
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": testimonials.map((t) => ({
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": t.name
      },
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": t.rating,
        "bestRating": "5"
      },
      "reviewBody": t.text,
      "datePublished": t.created_at
    }))
  } : null;

  if (isLoading || !testimonials || testimonials.length === 0) {
    return null;
  }

  return (
    <section className="py-6 md:py-8 bg-muted/30" aria-labelledby="testimonials-heading">
      {/* Structured Data */}
      {structuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        />
      )}

      <motion.div 
        className="container mx-auto px-4"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        {/* Section Header */}
        <div className="text-center mb-5">
          <Badge variant="secondary" className="mb-3">
            <Heart className="h-3 w-3 mr-1 fill-primary text-primary" />
            {badge}
          </Badge>
          <h2 id="testimonials-heading" className="font-display text-xl md:text-2xl font-bold text-foreground mb-2">
            {title}
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto text-sm mb-4">
            {description}
          </p>
          
          {/* Trust Indicators */}
          <div className="flex items-center justify-center gap-2 text-sm">
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className="h-5 w-5 text-yellow-400 fill-yellow-400"
                  aria-hidden="true"
                />
              ))}
            </div>
            <span className="font-semibold text-foreground">{averageRating}</span>
            <span className="text-muted-foreground">from {customerCount} customers</span>
          </div>
        </div>

        {/* Testimonials Carousel */}
        <div className="relative">
          <Carousel
            setApi={setApi}
            opts={{
              align: "start",
              loop: true,
            }}
            plugins={[
              Autoplay({
                delay: 4000,
                stopOnInteraction: true,
                stopOnMouseEnter: true,
              }),
            ]}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {testimonials.map((testimonial) => (
                <CarouselItem key={testimonial.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                  <TestimonialCard testimonial={testimonial} />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          
          {/* Navigation Arrows */}
          <div className="flex items-center justify-center gap-4 mt-5">
            <Button
              variant="outline"
              size="icon"
              onClick={scrollPrev}
              disabled={!canScrollPrev}
              className="rounded-full"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={scrollNext}
              disabled={!canScrollNext}
              className="rounded-full"
              aria-label="Next testimonial"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </div>

      </motion.div>
    </section>
  );
};

interface TestimonialCardProps {
  testimonial: Testimonial;
}

const TestimonialCard = ({ testimonial }: TestimonialCardProps) => {
  return (
    <Card className="h-full bg-card border-border/50 hover:shadow-card transition-all duration-300 overflow-hidden p-4">
      {/* Quote Icon & Rating Row */}
      <div className="flex items-center justify-between mb-3">
        <Quote className="h-6 w-6 text-primary/20" aria-hidden="true" />
        <div className="flex items-center gap-0.5" role="img" aria-label={`${testimonial.rating} out of 5 stars`}>
          {[1, 2, 3, 4, 5].map((star) => (
            <Star
              key={star}
              className={`h-3.5 w-3.5 ${
                star <= testimonial.rating
                  ? "text-yellow-400 fill-yellow-400"
                  : "text-muted-foreground/30"
              }`}
              aria-hidden="true"
            />
          ))}
        </div>
      </div>

      {/* Review Text */}
      <blockquote className="text-foreground/90 mb-3 text-sm leading-relaxed line-clamp-3">
        "{testimonial.text}"
      </blockquote>

      {/* Product */}
      {testimonial.product && (
        <p className="text-xs text-muted-foreground mb-3">
          Purchased: <span className="text-primary">{testimonial.product}</span>
        </p>
      )}

      {/* Author */}
      <div className="flex items-center gap-2.5 pt-3 border-t border-border/50">
        <Avatar className="h-8 w-8">
          <AvatarImage src={testimonial.avatar_url || ""} alt={testimonial.name} />
          <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
            {getInitials(testimonial.name)}
          </AvatarFallback>
        </Avatar>
        <div>
          <p className="font-medium text-foreground text-sm leading-tight">{testimonial.name}</p>
          {testimonial.location && (
            <p className="text-xs text-muted-foreground leading-tight">{testimonial.location}</p>
          )}
        </div>
      </div>
    </Card>
  );
};

export default Testimonials;
